import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, SlidersHorizontal } from 'lucide-react';
import {
  fetchQuotationList,
  generateQuotations,
  type QuotationListItemDto,
  type QuotationListStatus
} from '../lib/quotationApi';
import { QuotationTable } from '../features/quotations/QuotationTable';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import Button from '../components/ui/Button';
import SearchInput from '../components/ui/SearchInput';
import { ErrorState } from '../components/ui/States';
import DateInput from "../components/DateInput";

const QUOTATIONS_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>Quotations are created from received RFQs.</p>
    <p className="font-medium">The system follows this logic to create a quotation:</p>
    <ol className="list-decimal list-inside space-y-1 pl-1">
      <li>Finds the item price from historical quotations</li>
      <li>If not found, searches Pantry via their API</li>
      <li>If not found, searches the price list</li>
      <li>When a new vendor invoice is uploaded, quotations within last 21 days except Sent and Finalized are automatically updated based on the latest vendor invoice</li>
    </ol>
    <p>You can update each cell of quotation table.</p>
    <p className="font-medium">Each quotation has status:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li><strong>Needs Review:</strong> This is the default status. Whenever a quotation is generated, it is set to Needs Review so you can review and update the details.</li>
      <li><strong>Finalized:</strong> Once you have reviewed the quotation and are satisfied with the details, you can mark it as Finalized.</li>
      <li><strong>Sent:</strong> When you send the finalized quotation through the system, the status is automatically updated to Sent. If you export and send the quotation manually, you will need to update the status to Sent yourself.</li>
      <li><strong>Rejected:</strong> If the client rejects the quotation and cancels the order, you can manually change the status to Rejected.</li>
      <li><strong>Confirmed:</strong> Once the client's order is confirmed, every version of the quotation shows Confirmed with a link to the order, and its status can no longer be changed here. If the order is cancelled, the quotation shows its own status again.</li>
    </ul>
    <p className="font-medium">Markup is calculated in the following order:</p>
    <ol className="list-decimal list-inside space-y-1 pl-1">
      <li>From past quotations</li>
      <li>If not found, the system uses the cost price from Pantry and applies a default 30% markup</li>
      <li>If the item is not available in Pantry, the system refers to the price lists</li>
    </ol>
  </div>
);

// D161.3: Confirmed is worked out by the server (the RFQ has an order); the other four leave those out.
const STATUS_OPTIONS: { value: QuotationListStatus; label: string; style: string }[] = [
  { value: 'needs_review', label: 'Needs Review', style: 'bg-[#F8EDD1] text-[#D4B260]' },
  { value: 'finalized', label: 'Finalized', style: 'bg-[#EDF1FD] text-[#4676EA]' },
  { value: 'sent', label: 'Sent', style: 'bg-[#D6E8F4] text-primary-500' },
  { value: 'rejected', label: 'Rejected', style: 'bg-[#FAEDE7] text-[#CC4B13]' },
  { value: 'confirmed', label: 'Confirmed', style: 'bg-emerald-50 text-emerald-800' },
];

type Filters = {
  statuses: QuotationListStatus[];
  customer: string;
  // Deborah, 2026-09-04, asked directly whether the client or the vessel
  // mattered more and chose the vessel: "from this client we could get a
  // numerous number of vessels, same client". Filtered server-side and paged
  // like every other filter here, so it stays correct past page one.
  vessel: string;
  dateFrom: string;
  dateTo: string;
};

const EMPTY_FILTERS: Filters = { statuses: [], customer: '', vessel: '', dateFrom: '', dateTo: '' };

export const QuotationsPage: React.FC = () => {
  const [quotations, setQuotations] = useState<QuotationListItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Always-visible search (subject / client name / email). Debounced before querying.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Committed filters that drive the query.
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

  // Filter popover state + draft (working copy applied on "Apply").
  const [filterOpen, setFilterOpen] = useState(false);
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const filterRef = useRef<HTMLDivElement | null>(null);

  // Debounce the search box so we don't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Close the filter popover on outside click.
  useEffect(() => {
    if (!filterOpen) return;
    const onClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [filterOpen]);

  const loadQuotations = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit: pagination.limit,
        search: debouncedSearch || undefined,
        status: filters.statuses.length ? filters.statuses.join(',') : undefined,
        customer: filters.customer.trim() || undefined,
        vessel: filters.vessel.trim() || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc' as const,
      };

      const response = await fetchQuotationList(params);

      if (response.success) {
        setQuotations(response.data.quotations);
        setPagination(response.data.pagination);
      } else {
        throw new Error('Failed to fetch quotations');
      }
    } catch (err: any) {
      console.error('Failed to load quotations:', err);
      setError(err.message || 'Failed to load quotations');
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuotations = async () => {
    try {
      setGenerating(true);
      const response = await generateQuotations();

      if (response.success) {
        await loadQuotations(pagination.page);
        alert(`Quotation generation completed: ${response.data?.successful || 0} successful, ${response.data?.failed || 0} failed`);
      } else {
        throw new Error(response.message || 'Failed to generate quotations');
      }
    } catch (err: any) {
      console.error('Failed to generate quotations:', err);
      alert(`Error generating quotations: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const handlePageChange = (page: number) => {
    loadQuotations(page);
  };

  // Reload (back to page 1) whenever the committed filters or debounced search change.
  useEffect(() => {
    loadQuotations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, debouncedSearch]);

  const openFilters = () => {
    setDraft(filters);
    setFilterOpen((v) => !v);
  };

  const toggleDraftStatus = (status: QuotationListStatus) => {
    setDraft((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status],
    }));
  };

  const applyFilters = () => {
    setFilters(draft);
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setFilterOpen(false);
  };

  const activeFilterCount =
    filters.statuses.length +
    (filters.customer.trim() ? 1 : 0) +
    (filters.vessel.trim() ? 1 : 0) +
    (filters.dateFrom || filters.dateTo ? 1 : 0);

  return (
    <div className="h-full flex flex-col bg-brand-bg overflow-hidden">
      {/* Header */}
      {/* The same header band, title and controls as every other module
          (docs/DESIGN.md). The title is the sidebar's own label: "Your Quotes"
          was a second name for the screen people click "Quote Generator" to
          reach. */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">
              Quote Generator
            </h1>
            <ModuleInfoButton content={QUOTATIONS_INFO_CONTENT} ariaLabel="Quotations module information" />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search subject, client or vessel"
              className="w-full sm:w-[280px]"
            />

            {/* Filter button + popover */}
            <div className="relative" ref={filterRef}>
              <Button
                onClick={openFilters}
                icon={<SlidersHorizontal aria-hidden="true" className="h-4 w-4" />}
                className={activeFilterCount > 0 ? 'border-primary-500 bg-primary-50 text-primary-500' : ''}
              >
                Filters
                {activeFilterCount > 0 && (
                  <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary-500 px-1 text-xs font-semibold text-white">
                    {activeFilterCount}
                  </span>
                )}
              </Button>

              {filterOpen && (
                <div className="absolute right-0 top-full mt-2 w-[320px] max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-4">
                  {/* Status */}
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-slate-600 mb-2">Status</div>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((opt) => {
                        const active = draft.statuses.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => toggleDraftStatus(opt.value)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                              active
                                ? `${opt.style} border-transparent ring-2 ring-offset-1 ring-primary-500`
                                : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Client name */}
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-slate-600 mb-2">
                      Client name
                    </label>
                    <input
                      type="text"
                      value={draft.customer}
                      onChange={(e) => setDraft((prev) => ({ ...prev, customer: e.target.value }))}
                      placeholder="e.g. Kalpesh Matang"
                      className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  {/* Vessel name */}
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-slate-600 mb-2">
                      Vessel name
                    </label>
                    <input
                      type="text"
                      value={draft.vessel}
                      onChange={(e) => setDraft((prev) => ({ ...prev, vessel: e.target.value }))}
                      placeholder="e.g. MV NORDIC"
                      className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  {/* Date range */}
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-slate-600 mb-2">Created date</div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex flex-col gap-1">
                        <span className="text-xs text-slate-500">From</span>
                        <DateInput validity="filter"
                          value={draft.dateFrom}
                          max={draft.dateTo || undefined}
                          onChange={(v) => setDraft((prev) => ({ ...prev, dateFrom: v }))}
                          className="h-9 px-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-xs text-slate-500">To</span>
                        <DateInput validity="filter"
                          value={draft.dateTo}
                          min={draft.dateFrom || undefined}
                          onChange={(v) => setDraft((prev) => ({ ...prev, dateTo: v }))}
                          className="h-9 px-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-1">
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      Clear all
                    </Button>
                    <Button variant="primary" size="sm" onClick={applyFilters}>
                      Apply filters
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={() => loadQuotations(pagination.page)}
              loading={loading}
              icon={<RefreshCw aria-hidden="true" className="h-4 w-4" />}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {loading ? (
          <div className="h-64 rounded-lg border border-slate-200 bg-white">
            <LoadingState text="Loading quotations..." size={80} />
          </div>
        ) : error ? (
          <ErrorState message={error} onRetry={() => loadQuotations(pagination.page)} />
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white">
            <QuotationTable
              quotations={quotations}
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};
